import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import { StylesProvider } from '@material-ui/core';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { SnackbarProvider } from 'notistack';
import {
  RouteContext,
  PortalConfigContext,
  FlagsContext,
  PortalConfig,
} from '../../context';
import { ChoosePlanAlertDialog } from '../ChoosePlanAlertDialog';
import { useInitApp } from './useInitApp';
import Notifier from '../AlertSnackbar/Notifier';
import { WebsocketInitializer } from './WebsocketInitializer';

interface AppProps {
  context: {
    insertCss: (...styles: any[]) => () => void;
    pathname: string;
    query: Record<string, any>;
    isMobile: boolean;
    store: any;
    stripeContext: { publishableKey: string };
    portalConfig: any;
    flags: any;
  };
  children: React.ReactNode;
}
/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <Layout>
 *         <LandingPage />
 *       </Layout>
 *     </App>,
 *     container,
 *   );
 */

const App: React.FC<AppProps> = ({ context, children }) => {
  // NOTE: If you need to add or modify header, footer etc. of the app,
  // please do that inside the Layout component.
  const {
    pathname,
    query,
    isMobile,
    insertCss,
    store,
    stripeContext,
    portalConfig,
    flags,
  } = context;

  const routeContext = React.useMemo(() => ({ pathname, query, isMobile }), [
    pathname,
    query,
    isMobile,
  ]);

  const styleContextValue = React.useMemo(() => ({ insertCss }), [insertCss]);

  const [stripePromise] = React.useState(
    loadStripe(stripeContext.publishableKey),
  );

  const { id: userId, loaded: userLoaded } = store.getState().user;
  const apiEndpoints = (portalConfig as PortalConfig).AWS.API.endpoints;
  const chatClient = React.useMemo(() => {
    const client = new StreamChat(portalConfig.Chat.ClientId, {
      timeout: 6000,
    });
    client.setBaseURL('https://chat.stream-io-api.com');
    return client;
  }, []);

  useInitApp();

  return (
    <PortalConfigContext.Provider value={portalConfig}>
      <StyleContext.Provider value={styleContextValue}>
        <ReduxProvider store={store}>
          <Elements stripe={stripePromise}>
            <Chat client={chatClient} theme="portal-chat">
              <StylesProvider>
                <FlagsContext.Provider value={flags}>
                  <RouteContext.Provider value={routeContext}>
                    <ChoosePlanAlertDialog />
                    <SnackbarProvider
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      maxSnack={2}
                    >
                      <Notifier />
                      <WebsocketInitializer
                        userId={userId}
                        userLoaded={userLoaded}
                        apiEndpoints={apiEndpoints}
                      />
                      {React.Children.only(children)}
                    </SnackbarProvider>
                  </RouteContext.Provider>
                </FlagsContext.Provider>
              </StylesProvider>
            </Chat>
          </Elements>
        </ReduxProvider>
      </StyleContext.Provider>
    </PortalConfigContext.Provider>
  );
};

export default App;
